/**
 * Shared types — the contract between all platform services (API, web, workers).
 *
 * Zod schemas are the single source of truth — TypeScript types are inferred from them.
 *
 * Importing:
 *   Frontend:  import { UserSchema, type User } from '@/types'
 *   Backend:   import { CreateProjectSchema, type CreateProject } from '@/types'
 *   Workers:   import type { AgentJobData, JobEvent } from '@rocketships/types'
 *
 * Guidelines:
 *  - All request/response shapes live here, not in web/, api/, or workers/
 *  - Define Zod schemas first, infer types with z.infer<>
 *  - Keep this file framework-agnostic — no NestJS, no React, no Drizzle imports
 *  - No circular dependencies
 */
import { z } from 'zod/v3'

// ── Auth ──────────────────────────────────────────────────────────────────────

export const UserRoleSchema = z.enum(['owner', 'admin', 'member', 'viewer'])
export type UserRole = z.infer<typeof UserRoleSchema>

export const AuthProviderSchema = z.enum(['github', 'google'])
export type AuthProvider = z.infer<typeof AuthProviderSchema>

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  authProvider: AuthProviderSchema,
  providerAccountId: z.string(),
  avatarUrl: z.string().url().optional(),
  role: UserRoleSchema,
  orgId: z.string().uuid().optional(),
  isTestUser: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type User = z.infer<typeof UserSchema>

/** Minimal user context injected into request by JwtStrategy */
export const AuthenticatedUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  role: z.string(),
  orgId: z.string().uuid(),
})
export type AuthenticatedUser = z.infer<typeof AuthenticatedUserSchema>

export const OAuthCallbackSchema = z.object({
  code: z.string(),
  state: z.string(),
})
export type OAuthCallback = z.infer<typeof OAuthCallbackSchema>

export const ExchangeCodeSchema = z.object({
  code: z.string(),
})
export type ExchangeCode = z.infer<typeof ExchangeCodeSchema>

export const TokenResponseSchema = z.object({
  token: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int(),
  /** True when this login created a new account (signup), false for returning users */
  isNewUser: z.boolean().optional(),
})
export type TokenResponse = z.infer<typeof TokenResponseSchema>

// ── Permissions ───────────────────────────────────────────────────────────────

/**
 * All permission slugs used in the platform.
 * Checked against org_permissions table (union of org-wide + user-specific grants).
 * Users with membership role 'owner' bypass all permission checks.
 */
export const PERMISSION_SLUGS = {
  PROJECT_CREATE: 'project.create',
  PROJECT_DELETE: 'project.delete',
  PROJECT_RELEASE: 'project.release',
  DRAFT_CREATE: 'draft.create',
  DRAFT_DELETE: 'draft.delete',
  DRAFT_MERGE: 'draft.merge',
  JOB_CANCEL: 'job.cancel',
} as const

export type PermissionSlug = (typeof PERMISSION_SLUGS)[keyof typeof PERMISSION_SLUGS]

export const OrgPermissionSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  userId: z.string().uuid().nullable(),
  slug: z.string(),
  createdAt: z.string().datetime(),
})
export type OrgPermission = z.infer<typeof OrgPermissionSchema>

// ── Organizations ─────────────────────────────────────────────────────────────

export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type Organization = z.infer<typeof OrganizationSchema>

export const CreateOrganizationSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1).optional(),
})
export type CreateOrganization = z.infer<typeof CreateOrganizationSchema>

// ── Job Status (defined early — referenced by Projects and Jobs) ─────────────

export const JobStatusSchema = z.enum(['queued', 'running', 'completed', 'failed', 'cancelling', 'cancelled'])
export type JobStatus = z.infer<typeof JobStatusSchema>

// ── Projects ──────────────────────────────────────────────────────────────────

export const OnboardStatusSchema = z.enum(['pending', 'reverting', 'completed'])
export type OnboardStatus = z.infer<typeof OnboardStatusSchema>

export const LatestJobSchema = z.object({
  action: z.string(),
  status: JobStatusSchema,
  createdAt: z.string().datetime(),
})
export type LatestJob = z.infer<typeof LatestJobSchema>

export const ProjectSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  repoUrl: z.string().url().optional(),
  repoFullName: z.string().optional(),
  installationId: z.number().int().optional(),
  releaseBranch: z.string(),
  integrationBranch: z.string().optional(),
  prNumber: z.number().int().optional(),
  prUrl: z.string().url().optional(),
  onboardStatus: OnboardStatusSchema.nullable().optional(),
  onboardStep: z.number().int().min(0).max(5).optional(),
  revertingToStep: z.number().int().min(0).max(3).nullable().optional(),
  useDeepResearch: z.boolean().optional(),
  deepResearchStatus: z.enum(['not-enabled', 'pending', 'prompt-generating', 'prompt-ready', 'researching', 'complete']).optional(),
  deepResearchJobId: z.string().uuid().nullable().optional(),
  onboardPath: z.string().optional(),
  analyzeJobId: z.string().uuid().nullable().optional(),
  strawpersonJobId: z.string().uuid().nullable().optional(),
  scaffoldJobId: z.string().uuid().nullable().optional(),
  interviewJobId: z.string().uuid().nullable().optional(),
  latestJob: LatestJobSchema.nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type Project = z.infer<typeof ProjectSchema>

export const CreateProjectSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  repoName: z.string().optional(),
  installationId: z.number().int().optional(),
  repoIsPrivate: z.boolean().optional(),
  connectExistingRepo: z.string().optional(),
  skipGitHub: z.boolean().optional(),
})
export type CreateProject = z.infer<typeof CreateProjectSchema>

export const UpdateProjectSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
})
export type UpdateProject = z.infer<typeof UpdateProjectSchema>

// ── Drafts ────────────────────────────────────────────────────────────────────

export const DraftStatusSchema = z.enum([
  'open',
  'feedback',
  'planning',
  'approved',
  'building',
  'built',
  'deploying',
  'deployed',
  'merged',
])
export type DraftStatus = z.infer<typeof DraftStatusSchema>

export const DraftSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  intent: z.string(),
  branchName: z.string(),
  status: DraftStatusSchema,
  mergeCommitSha: z.string().nullable(),
  deploymentUrl: z.string().nullable(),
  activeJobId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type Draft = z.infer<typeof DraftSchema>

export const CreateDraftSchema = z.object({
  intent: z.string().min(1),
})
export type CreateDraft = z.infer<typeof CreateDraftSchema>

export const TriggerWorkflowSchema = z.object({
  action: z.enum(['feedback', 'plan', 'build', 'fix', 'fix-kb', 'merge', 'deploy']),
  taskFilter: z.string().optional(),
  focus: z.string().optional(),
  prompt: z.string().optional(),
})
export type TriggerWorkflow = z.infer<typeof TriggerWorkflowSchema>

// ── Jobs ──────────────────────────────────────────────────────────────────────

export const JobEventSchema = z.object({
  timestamp: z.string(),
  type: z.string(),
  message: z.string(),
  data: z.record(z.unknown()).optional(),
})
export type JobEvent = z.infer<typeof JobEventSchema>

export const JobSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  action: z.string(),
  status: JobStatusSchema,
  events: z.array(JobEventSchema),
  result: z.unknown().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type Job = z.infer<typeof JobSchema>

// ── Job payloads (API dispatches, workers consume) ───────────────────────────

/** Payload for agent workflow jobs (build, fix, feedback, plan, fix-kb) */
export interface AgentJobData {
  jobId: string
  projectId: string
  triggeredByUserId?: string
  action: string
  draftName: string
  branchName?: string
  taskFilter?: string
  focus?: string
  file?: string
  prompt?: string
  isPlatform?: boolean
  /** User who triggered the job — used for analytics tracking */
  userId?: string
  /** Org the project belongs to — used for analytics tracking */
  orgId?: string
}

export const ImproveKBFileSchema = z.object({
  path: z.string().min(1),
  prompt: z.string().optional(),
  branch: z.string().optional(),
})
export type ImproveKBFile = z.infer<typeof ImproveKBFileSchema>

// ── Deployments ───────────────────────────────────────────────────────────────

export const DeploymentEnvironmentSchema = z.enum(['preview', 'dev', 'production'])
export type DeploymentEnvironment = z.infer<typeof DeploymentEnvironmentSchema>

export const DeploymentStatusSchema = z.enum([
  'pending',
  'deploying',
  'live',
  'failed',
  'stopped',
])
export type DeploymentStatus = z.infer<typeof DeploymentStatusSchema>

export const DeploymentSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  draftId: z.string().uuid().optional(),
  environment: DeploymentEnvironmentSchema,
  frontendUrl: z.string().url().nullable(),
  apiUrl: z.string().url().nullable(),
  status: DeploymentStatusSchema,
  commitSha: z.string(),
  pullRequestId: z.string().uuid().nullable(),
  deletedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})
export type Deployment = z.infer<typeof DeploymentSchema>

/** Payload for deployment jobs */
export interface DeployJobData {
  jobId: string
  projectId: string
  action: string
  draftId?: string
  draftName: string
  branchName?: string
  environment?: DeploymentEnvironment
  projectSlug?: string
  workspacePath?: string
}

/** Payload for teardown jobs */
export interface TeardownJobData {
  jobId: string
  projectId: string
  environment: string
  projectSlug: string
  prNumber?: number
  draftId?: string
  draftName?: string
  reason: 'draft_merge' | 'draft_delete' | 'project_delete'
}

// ── Workspaces (internal — not exposed via API) ──────────────────────────────

export const WorkspaceSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  branchName: z.string(),
  path: z.string(),
  lastAccessedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
})
export type Workspace = z.infer<typeof WorkspaceSchema>

// ── Draft Artifacts ───────────────────────────────────────────────────────────

export const DraftReviewSchema = z.object({
  summary: z.string().nullable(),
  ba: z.string().nullable(),
  pm: z.string().nullable(),
  architect: z.string().nullable(),
})
export type DraftReview = z.infer<typeof DraftReviewSchema>

export const DraftPlanSummarySchema = z.object({
  discovery: z.string().nullable(),
  impact: z.string().nullable(),
  tasks: z.string().nullable(),
})
export type DraftPlanSummary = z.infer<typeof DraftPlanSummarySchema>

export const BuildSummaryEnvVarSchema = z.object({
  name: z.string(),
  description: z.string(),
  example: z.string().optional(),
})
export type BuildSummaryEnvVar = z.infer<typeof BuildSummaryEnvVarSchema>

export const BuildSummaryTestStepSchema = z.object({
  step: z.string(),
  expected: z.string().optional(),
})
export type BuildSummaryTestStep = z.infer<typeof BuildSummaryTestStepSchema>

export const BuildSummarySetupStepSchema = z.object({
  title: z.string(),
  instructions: z.string(),
})
export type BuildSummarySetupStep = z.infer<typeof BuildSummarySetupStepSchema>

export const BuildSummarySchema = z.object({
  testPlan: z.array(BuildSummaryTestStepSchema),
  envVars: z.array(BuildSummaryEnvVarSchema),
  setupSteps: z.array(BuildSummarySetupStepSchema),
})
export type BuildSummary = z.infer<typeof BuildSummarySchema>

export const DraftArtifactsSchema = z.object({
  review: DraftReviewSchema,
  plan: DraftPlanSummarySchema,
  buildSummary: BuildSummarySchema.nullable(),
  buildReview: z.string().nullable(),
  buildValidation: z.string().nullable(),
})
export type DraftArtifacts = z.infer<typeof DraftArtifactsSchema>

// ── Diff ──────────────────────────────────────────────────────────────────────

export const DiffLineSchema = z.object({
  type: z.enum(['add', 'remove', 'context']),
  content: z.string(),
  oldLineNumber: z.number().int().optional(),
  newLineNumber: z.number().int().optional(),
})
export type DiffLine = z.infer<typeof DiffLineSchema>

export const DiffHunkSchema = z.object({
  header: z.string(),
  lines: z.array(DiffLineSchema),
})
export type DiffHunk = z.infer<typeof DiffHunkSchema>

export const DiffFileSchema = z.object({
  path: z.string(),
  hunks: z.array(DiffHunkSchema),
})
export type DiffFile = z.infer<typeof DiffFileSchema>

export const DiffResponseSchema = z.object({
  files: z.array(DiffFileSchema),
})
export type DiffResponse = z.infer<typeof DiffResponseSchema>

// ── GitHub Installations ─────────────────────────────────────────────────────

export const GitHubInstallationSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  installationId: z.number().int(),
  accountLogin: z.string(),
  accountType: z.enum(['Organization', 'User']),
  installedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
})
export type GitHubInstallation = z.infer<typeof GitHubInstallationSchema>

export const GitHubInstallationsResponseSchema = z.array(GitHubInstallationSchema)
export type GitHubInstallationsResponse = z.infer<typeof GitHubInstallationsResponseSchema>

// ── Webhooks ──────────────────────────────────────────────────────────────────

/** Loose schema — GitHub webhook payloads vary widely by event type */
export const WebhookEventSchema = z.object({
  action: z.string().optional(),
  repository: z.unknown().optional(),
  installation: z.unknown().optional(),
  pull_request: z.unknown().optional(),
  ref: z.string().optional(),
  commits: z.unknown().optional(),
})
export type WebhookEvent = z.infer<typeof WebhookEventSchema>

// ── Releases ──────────────────────────────────────────────────────────────────

export const CreateReleaseSchema = z.object({
  version: z.string().optional(),
  notes: z.string().optional(),
})
export type CreateRelease = z.infer<typeof CreateReleaseSchema>

export const ReleaseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  version: z.string(),
  commitSha: z.string(),
  releaseUrl: z.string().url(),
  deploymentId: z.string().uuid(),
  createdAt: z.string().datetime(),
})
export type Release = z.infer<typeof ReleaseSchema>

// ── Onboarding ───────────────────────────────────────────────────────────────

export const StartOnboardSchema = z.object({
  name: z.string().min(1),
  idea: z.string().min(1),
  repoName: z.string().optional(),
  installationId: z.number().int().optional(),
  repoIsPrivate: z.boolean().optional(),
  connectExistingRepo: z.string().optional(),
  skipGitHub: z.boolean().optional(),
  useDeepResearch: z.boolean().optional(),
})
export type StartOnboard = z.infer<typeof StartOnboardSchema>

export const OnboardResultSchema = z.object({
  projectId: z.string().uuid(),
  jobId: z.string().uuid(),
  slug: z.string(),
})
export type OnboardResult = z.infer<typeof OnboardResultSchema>

export const OnboardJobResultSchema = z.object({
  jobId: z.string().uuid(),
})
export type OnboardJobResult = z.infer<typeof OnboardJobResultSchema>

export const FinishOnboardResultSchema = z.object({
  draftId: z.string().uuid().nullable(),
  projectId: z.string().uuid(),
})
export type FinishOnboardResult = z.infer<typeof FinishOnboardResultSchema>

/** Onboard state fields — used by reset/revert flows and legacy consumers. */
export const OnboardStateSchema = z.object({
  projectId: z.string().uuid(),
  slug: z.string(),
  onboardStatus: OnboardStatusSchema,
  onboardStep: z.number().int().min(0).max(5),
  revertingToStep: z.number().int().min(0).max(3).nullable(),
  analyzeJobId: z.string().uuid().nullable(),
  strawpersonJobId: z.string().uuid().nullable(),
  scaffoldJobId: z.string().uuid().nullable(),
  interviewJobId: z.string().uuid().nullable(),
})
export type OnboardState = z.infer<typeof OnboardStateSchema>

export const ResetOnboardSchema = z.object({
  step: z.enum(['interview', 'strawperson', 'debate', 'scaffold']),
})
export type ResetOnboard = z.infer<typeof ResetOnboardSchema>

// Factor breakdowns for each verdict dimension (new expanded format)
const ProblemClarityFactorsSchema = z.object({
  specificity: z.number().min(0).max(100),
  evidenceOfPain: z.number().min(0).max(100),
  willingnessToPay: z.number().min(0).max(100),
  urgency: z.number().min(0).max(100),
})
const MarketOpportunityFactorsSchema = z.object({
  size: z.number().min(0).max(100),
  growth: z.number().min(0).max(100),
  accessibility: z.number().min(0).max(100),
  timing: z.number().min(0).max(100),
})
const DifferentiationFactorsSchema = z.object({
  uniqueInsight: z.number().min(0).max(100),
  moatPotential: z.number().min(0).max(100),
  positioningClarity: z.number().min(0).max(100),
})
const FeasibilityFactorsSchema = z.object({
  technicalComplexity: z.number().min(0).max(100),
  regulatoryRisk: z.number().min(0).max(100),
  operationalComplexity: z.number().min(0).max(100),
})

// Dimension score: either a plain number (legacy) or an object with score + factors (expanded)
const DimensionScoreSchema = z.union([
  z.number().min(0).max(100),
  z.object({ score: z.number().min(0).max(100), factors: z.record(z.number().min(0).max(100)) }),
])

export const VerdictScoresSchema = z.object({
  problemClarity: DimensionScoreSchema,
  marketOpportunity: DimensionScoreSchema,
  differentiation: DimensionScoreSchema,
  feasibility: DimensionScoreSchema,
  overall: z.number().min(0).max(100),
  winner: z.enum(['pro', 'con']),
  criticalFailure: z.boolean().optional(),
})
export type VerdictScores = z.infer<typeof VerdictScoresSchema>

// Factor schemas exported for consumers that need typed access
export type ProblemClarityFactors = z.infer<typeof ProblemClarityFactorsSchema>
export type MarketOpportunityFactors = z.infer<typeof MarketOpportunityFactorsSchema>
export type DifferentiationFactors = z.infer<typeof DifferentiationFactorsSchema>
export type FeasibilityFactors = z.infer<typeof FeasibilityFactorsSchema>

/** Extract the numeric score from a dimension value (handles both legacy and expanded format) */
export function getDimensionScore(value: number | { score: number; factors: Record<string, number> }): number {
  return typeof value === 'number' ? value : value.score
}

// ── Interview (onboard interview step) ──────────────────────────────────────

export const InterviewQuestionCategorySchema = z.enum(['customer', 'problem', 'market'])
export type InterviewQuestionCategory = z.infer<typeof InterviewQuestionCategorySchema>

export const InterviewQuestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  category: InterviewQuestionCategorySchema,
  suggestedAnswers: z.array(z.string()).min(2).max(5),
})
export type InterviewQuestion = z.infer<typeof InterviewQuestionSchema>

export const InterviewQuestionsSchema = z.object({
  questions: z.array(InterviewQuestionSchema).max(5),
})
export type InterviewQuestions = z.infer<typeof InterviewQuestionsSchema>

export const InterviewResponseSchema = z.object({
  questionId: z.string(),
  response: z.string(),
})
export type InterviewResponse = z.infer<typeof InterviewResponseSchema>

export const InterviewResponsesSchema = z.object({
  responses: z.array(InterviewResponseSchema),
})
export type InterviewResponses = z.infer<typeof InterviewResponsesSchema>

// ── KB (file serving from workspace) ─────────────────────────────────────────

export interface KBTreeNode {
  name: string
  path: string
  type: 'file' | 'directory'
  status: 'complete' | 'partial' | 'empty'
  children?: KBTreeNode[] | undefined
}

export const KBTreeNodeSchema: z.ZodType<KBTreeNode> = z.object({
  name: z.string(),
  path: z.string(),
  type: z.enum(['file', 'directory']),
  status: z.enum(['complete', 'partial', 'empty']),
  children: z.lazy(() => z.array(KBTreeNodeSchema)).optional(),
})

export const KBFileSchema = z.object({
  path: z.string(),
  content: z.string(),
})
export type KBFile = z.infer<typeof KBFileSchema>

export const UpdateKBFileSchema = z.object({
  path: z.string(),
  content: z.string(),
  /** Optional branch name — defaults to 'dev'. */
  branch: z.string().optional(),
})
export type UpdateKBFile = z.infer<typeof UpdateKBFileSchema>

export const CreateKBFileSchema = z.object({
  /** Workspace-relative path e.g. "kb/product/features/my-feature.md". Must start with "kb/". */
  path: z.string().min(1),
  /** Optional branch name — defaults to 'dev'. */
  branch: z.string().optional(),
  /** Optional title — written as frontmatter `title:` and H1 in the body. */
  title: z.string().optional(),
  /** Optional description — written as frontmatter `description:`. */
  description: z.string().optional(),
})
export type CreateKBFile = z.infer<typeof CreateKBFileSchema>

export const DeleteKBFileSchema = z.object({
  /** Workspace-relative path e.g. "kb/product/features/my-feature.md". Must start with "kb/". */
  path: z.string().min(1),
  /** Optional branch name — defaults to 'dev'. */
  branch: z.string().optional(),
})
export type DeleteKBFile = z.infer<typeof DeleteKBFileSchema>

// ── API response wrappers ─────────────────────────────────────────────────────

/** Error response — consistent across all endpoints */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
})
export type ApiError = z.infer<typeof ApiErrorSchema>

// ── Pagination ────────────────────────────────────────────────────────────────

export const PaginationParamsSchema = z.object({
  page: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().optional(),
})
export type PaginationParams = z.infer<typeof PaginationParamsSchema>

export const SortParamsSchema = z.object({
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
})
export type SortParams = z.infer<typeof SortParamsSchema>

// ── Generic response wrappers (types only — generics don't map to Zod) ──────

/** Successful list response */
export interface ListResponse<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
}

/** Successful single-item response */
export interface ItemResponse<T> {
  data: T
}

// ── Credits ──────────────────────────────────────────────────────────────────

/** Credit expiry duration in milliseconds (12 months). */
export const CREDIT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000

// ── Billing ────────────────────────────────────────────────────────────────────

export const CreditBalanceSchema = z.object({
  balance: z.number().int(),
  thirtyDayUsage: z.number().int(),
  currency: z.literal('credits'),
})
export type CreditBalance = z.infer<typeof CreditBalanceSchema>

export const CreditTransactionSchema = z.object({
  id: z.string().uuid(),
  amount: z.number().int(),
  source: z.string(),
  sourceId: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  jobId: z.string().uuid().nullable().optional(),
  action: z.string().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  projectName: z.string().nullable().optional(),
  userName: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
})
export type CreditTransaction = z.infer<typeof CreditTransactionSchema>

export const CreditTransactionTypeSchema = z.enum(['grant', 'spend'])
export type CreditTransactionType = z.infer<typeof CreditTransactionTypeSchema>

// ── Pricing ──────────────────────────────────────────────────────────────────

export const CREDIT_PACKS = [
  { id: 'credits_5k', credits: 5_000, priceUsd: 5 },
  { id: 'credits_10k', credits: 10_000, priceUsd: 9 },
  { id: 'credits_25k', credits: 25_000, priceUsd: 20 },
  { id: 'credits_50k', credits: 50_000, priceUsd: 35, hidden: true },
  { id: 'credits_100k', credits: 100_000, priceUsd: 60, hidden: true },
] as const

export type CreditPackId = (typeof CREDIT_PACKS)[number]['id']

export const SUBSCRIPTION_PLAN = {
  id: 'monthly_pro',
  creditsPerMonth: 50_000,
  priceUsd: 29,
  features: [
    '50,000 credits per month',
    'Access to deep research',
    'Unlimited projects',
  ],
} as const

export const ALL_PACK_IDS = [
  ...CREDIT_PACKS.map((p) => p.id),
  SUBSCRIPTION_PLAN.id,
] as const

export const CreateCheckoutSessionSchema = z.object({
  packId: z.string(),
})
export type CreateCheckoutSession = z.infer<typeof CreateCheckoutSessionSchema>

export const SubscriptionStatusSchema = z.enum(['active', 'past_due', 'canceled', 'none'])
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>

export const AccountSubscriptionSchema = z.object({
  status: SubscriptionStatusSchema,
  currentPeriodEnd: z.string().datetime().nullable(),
  cancelAtPeriodEnd: z.boolean(),
})
export type AccountSubscription = z.infer<typeof AccountSubscriptionSchema>

export const AccountInfoSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    avatarUrl: z.string().url().optional(),
  }),
  credits: CreditBalanceSchema,
  projectCount: z.number().int(),
  projectLimit: z.number().int().nullable(),
  subscription: AccountSubscriptionSchema,
})
export type AccountInfo = z.infer<typeof AccountInfoSchema>

// ── Invite Codes ──────────────────────────────────────────────────────────────

export const InviteCodeStatusSchema = z.enum(['available', 'used'])
export type InviteCodeStatus = z.infer<typeof InviteCodeStatusSchema>

export const InviteCodeSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  createdBy: z.string().uuid(),
  usedBy: z.string().uuid().nullable(),
  status: InviteCodeStatusSchema,
  createdAt: z.string().datetime(),
  usedAt: z.string().datetime().nullable(),
})
export type InviteCode = z.infer<typeof InviteCodeSchema>

export const InviteCodeValidateResponseSchema = z.object({
  valid: z.boolean(),
  code: z.string().optional(),
})
export type InviteCodeValidateResponse = z.infer<typeof InviteCodeValidateResponseSchema>

// ── Waitlist ──────────────────────────────────────────────────────────────────

export const WaitlistEntrySchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  source: z.string(),
  createdAt: z.string().datetime(),
})
export type WaitlistEntry = z.infer<typeof WaitlistEntrySchema>

export const CreateWaitlistEntrySchema = z.object({
  email: z.string().email(),
  source: z.string().optional(),
})
export type CreateWaitlistEntry = z.infer<typeof CreateWaitlistEntrySchema>

export const WaitlistEntryResponseSchema = z.object({
  alreadyRegistered: z.boolean(),
})
export type WaitlistEntryResponse = z.infer<typeof WaitlistEntryResponseSchema>

// ── Admin ────────────────────────────────────────────────────────────────────

/** Request body for admin credit adjustment */
export const AdminCreditAdjustmentSchema = z.object({
  amount: z.number().int(),
  description: z.string().min(1),
})
export type AdminCreditAdjustment = z.infer<typeof AdminCreditAdjustmentSchema>

/** Response after admin credit adjustment */
export interface AdminCreditAdjustmentResponse {
  balance: number
}

/** Row in the admin users list table */
export interface AdminUserRow {
  id: string
  name: string
  email: string
  avatarUrl: string | null
  authProvider: string
  providerAccountId: string
  role: string
  orgName: string | null
  orgId: string | null
  createdAt: string
  lastLoginAt: string | null
}

/** Project summary shown on admin user detail */
export interface AdminProjectSummary {
  id: string
  name: string
  onboardStatus: string | null
  creditsUsed: number
  createdAt: string
}

/** Full admin user detail view */
export interface AdminUserDetail {
  id: string
  name: string
  email: string
  avatarUrl: string | null
  authProvider: string
  providerAccountId: string
  role: string
  isTestUser: boolean
  createdAt: string
  lastLoginAt: string | null
  org: {
    id: string
    name: string
    slug: string
    subscriptionStatus: string
    stripeCustomerId: string | null
  } | null
  projects: AdminProjectSummary[]
  credits: {
    balance: number
    thirtyDayUsage: number
  }
  stripeCustomerUrl: string | null
  recentTransactions: CreditTransaction[]
}

/** Row in the admin orgs list table */
export interface AdminOrgRow {
  id: string
  name: string
  slug: string
  createdAt: string
  userCount: number
  creditBalance: number
  creditsUsed: number
  subscriptionStatus: string
  stripeCustomerId: string | null
}

/** Member row shown on admin org detail */
export interface AdminOrgMember {
  id: string
  name: string
  email: string
  role: string
  lastLoginAt: string | null
}

// ── User Feedback ────────────────────────────────────────────────────────────

export const CreateFeedbackSchema = z.object({
  request: z.string().min(1).max(5000),
  posthogSessionId: z.string().nullable().optional(),
  currentUrl: z.string().nullable().optional(),
})
export type CreateFeedback = z.infer<typeof CreateFeedbackSchema>

export interface FeedbackRow {
  id: string
  userId: string
  userName: string
  userEmail: string
  request: string
  posthogSessionId: string | null
  currentUrl: string | null
  createdAt: string
}

/** Full admin org detail view */
export interface AdminOrgDetail {
  id: string
  name: string
  slug: string
  createdAt: string
  subscriptionStatus: string
  stripeCustomerId: string | null
  stripeCustomerUrl: string | null
  members: AdminOrgMember[]
  credits: {
    balance: number
    thirtyDayUsage: number
  }
  recentTransactions: CreditTransaction[]
}

